import { useState } from 'react';
import { storage } from '../lib/api';
import { useStorage, useDeleteStorageFile, useBulkDeleteStorage } from '../hooks/useStorage';
import { format } from 'date-fns';
import {
  HardDrive, Trash2, Download, Search, RefreshCw, CheckSquare, Square,
  ChevronRight, File, FileText, Image, Archive, AlertTriangle, BarChart3,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

function fileIcon(key: string) {
  if (key.endsWith('.html')) return FileText;
  if (key.endsWith('.zip')) return Archive;
  if (/\.(png|jpg|jpeg|avif|webp|gif)$/i.test(key)) return Image;
  return File;
}

function usageColor(pct: number): string {
  if (pct >= 90) return 'bg-red-500';
  if (pct >= 75) return 'bg-amber-500';
  if (pct >= 50) return 'bg-yellow-400';
  return 'bg-teal-500';
}

function usageTextColor(pct: number): string {
  if (pct >= 90) return 'text-red-700';
  if (pct >= 75) return 'text-amber-700';
  if (pct >= 50) return 'text-yellow-700';
  return 'text-teal-700';
}

function UsageBar({ label, current, limit, pct, unit }: {
  label: string; current: string; limit: string; pct: number; unit: string;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className={`text-sm font-semibold ${usageTextColor(pct)}`}>
          {current} / {limit} {unit}
        </span>
      </div>
      <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${usageColor(pct)}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <div className="text-right mt-0.5">
        <span className={`text-xs font-medium ${usageTextColor(pct)}`}>{pct}%</span>
      </div>
    </div>
  );
}

export default function StorageManagerPage() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [prefix, setPrefix] = useState('');

  const { data, isLoading: loading, refetch } = useStorage(prefix || undefined);
  const files = data?.files ?? [];
  const usage = data?.usage ?? null;

  const deleteMutation = useDeleteStorageFile();
  const bulkDeleteMutation = useBulkDeleteStorage();
  const deleting = bulkDeleteMutation.isPending;

  const filtered = search
    ? files.filter(f => f.key.toLowerCase().includes(search.toLowerCase()))
    : files;

  // Group by top-level prefix
  const prefixes = new Set<string>();
  files.forEach(f => {
    const parts = f.key.split('/');
    if (parts.length > 1 && parts[0]) prefixes.add(parts[0]);
  });

  const toggleSelect = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(f => f.key)));
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`¿Eliminar ${selected.size} archivo(s)? Esta acción no se puede deshacer.`)) return;

    bulkDeleteMutation.mutate(Array.from(selected), {
      onSuccess: () => setSelected(new Set()),
      onError: () => alert('Error al eliminar archivos'),
    });
  };

  const handleDeleteOne = async (key: string) => {
    if (!confirm(`¿Eliminar ${key}?`)) return;
    deleteMutation.mutate(key, {
      onSuccess: () => setSelected(prev => { const n = new Set(prev); n.delete(key); return n; }),
      onError: () => alert('Error al eliminar'),
    });
  };

  const allSelected = filtered.length > 0 && selected.size === filtered.length;

  return (
    <div className="space-y-6">
      <PageHeader
        type="storage"
        icon={HardDrive}
        title="Almacenamiento R2"
        subtitle="Gestión de archivos · Cloudflare R2"
        actions={
          <button
            onClick={() => refetch()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
        }
      />

      {/* Usage Dashboard */}
      {usage && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={18} className="text-teal-600" />
            <h2 className="text-lg font-semibold text-gray-900">Uso del Plan Gratuito</h2>
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{usage.month}</span>
          </div>

          {usage.alerts.length > 0 && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <span className="font-semibold">Atención:</span> Estás acercándote a los límites gratuitos de R2.
                {usage.percentages.storage >= 90 || usage.percentages.class_a >= 90 || usage.percentages.class_b >= 90
                  ? ' ¡Considera eliminar archivos innecesarios!'
                  : ' Monitorea el uso para evitar cargos.'}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <UsageBar
              label="Almacenamiento"
              current={formatBytes(usage.storage_bytes)}
              limit="10"
              pct={usage.percentages.storage}
              unit="GB"
            />
            <UsageBar
              label="Operaciones Clase A"
              current={formatNumber(usage.class_a_ops)}
              limit="1M"
              pct={usage.percentages.class_a}
              unit=""
            />
            <UsageBar
              label="Operaciones Clase B"
              current={formatNumber(usage.class_b_ops)}
              limit="10M"
              pct={usage.percentages.class_b}
              unit=""
            />
          </div>

          <p className="text-xs text-gray-400 mt-4">
            Clase A: PUT, LIST, DELETE · Clase B: GET, HEAD ·
            <a href="https://developers.cloudflare.com/r2/pricing/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline ml-1">
              Documentación de precios R2 →
            </a>
          </p>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        {/* Prefix breadcrumb */}
        <div className="flex items-center gap-1 text-sm">
          <button
            onClick={() => setPrefix('')}
            className={`px-2 py-1 rounded ${!prefix ? 'bg-teal-100 text-teal-700 font-medium' : 'text-gray-500 hover:text-teal-600'}`}
          >
            Raíz
          </button>
          {prefix && (
            <>
              <ChevronRight size={14} className="text-gray-400" />
              <span className="px-2 py-1 bg-teal-100 text-teal-700 font-medium rounded">{prefix}/</span>
            </>
          )}
          {!prefix && Array.from(prefixes).sort().map(p => (
            <button
              key={p}
              onClick={() => setPrefix(p)}
              className="px-2 py-1 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded"
            >
              {p}/
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar archivos..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
          />
        </div>

        {/* Bulk delete */}
        {selected.size > 0 && (
          <button
            onClick={handleBulkDelete}
            disabled={deleting}
            className="flex items-center gap-2 px-3 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <Trash2 size={16} />
            {deleting ? 'Eliminando...' : `Eliminar (${selected.size})`}
          </button>
        )}
      </div>

      {/* File list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[40px_1fr_100px_140px_80px] gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase">
          <button onClick={toggleAll} className="flex items-center justify-center">
            {allSelected ? <CheckSquare size={16} className="text-teal-600" /> : <Square size={16} />}
          </button>
          <span>Archivo</span>
          <span className="text-right">Tamaño</span>
          <span>Fecha</span>
          <span className="text-center">Acciones</span>
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-400">
            <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
            Cargando archivos...
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-400">
            <HardDrive size={32} className="mx-auto mb-2 opacity-50" />
            {search ? 'No se encontraron archivos' : 'El almacenamiento está vacío'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
            {filtered.map(file => {
              const Icon = fileIcon(file.key);
              const isSelected = selected.has(file.key);
              return (
                <div
                  key={file.key}
                  className={`grid grid-cols-[40px_1fr_100px_140px_80px] gap-2 px-4 py-2.5 items-center hover:bg-gray-50 text-sm ${
                    isSelected ? 'bg-teal-50/50' : ''
                  }`}
                >
                  <button onClick={() => toggleSelect(file.key)} className="flex items-center justify-center">
                    {isSelected
                      ? <CheckSquare size={16} className="text-teal-600" />
                      : <Square size={16} className="text-gray-300" />}
                  </button>
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon size={16} className="text-gray-400 flex-shrink-0" />
                    <span className="truncate text-gray-700" title={file.key}>{file.key}</span>
                  </div>
                  <span className="text-right text-gray-500 tabular-nums">{formatBytes(file.size)}</span>
                  <span className="text-gray-400 text-xs">
                    {format(new Date(file.uploaded), 'yyyy-MM-dd HH:mm')}
                  </span>
                  <div className="flex items-center justify-center gap-1">
                    <a
                      href={storage.download(file.key)}
                      className="p-1.5 text-gray-400 hover:text-teal-600 rounded"
                      title="Descargar"
                    >
                      <Download size={14} />
                    </a>
                    <button
                      onClick={() => handleDeleteOne(file.key)}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                      title="Eliminar"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex justify-between">
            <span>{filtered.length} archivo(s)</span>
            <span>Total: {formatBytes(filtered.reduce((s, f) => s + f.size, 0))}</span>
          </div>
        )}
      </div>
    </div>
  );
}
